import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import classes from "./SmallModal.module.css";

export const SmallModal = ({ 
        children, 
        open, 
        className, 
        onBackdropClick, 
        fromEditWishlist = false,
        fromListStudio = false,
        reviewModal = false,
        fromBookingDetails = false,
        fromDeleteStudio = false,
        fromRegisterPopUp = false,
        fromCreateService = false,
        fromVideoFoto = false,
        changeStudioModal = false,
        fromCreateEditService = false,
        equipmentOptionalSheet = false,
        paidEquipmentModal = false
    }) => {    

    const dialog = useRef();
    const closeTimeoutRef = useRef();
    const resizeHandlerRef = useRef(null);
    const originalOverflowRef = useRef('');

    const [isClosing, setIsClosing] = useState(false);

    useEffect(() => () => {
        clearTimeout(closeTimeoutRef.current);
        if (resizeHandlerRef.current) {
            window.removeEventListener('resize', resizeHandlerRef.current);
            resizeHandlerRef.current = null;
        }
        document.body.style.overflow = originalOverflowRef.current || '';
        const modal = dialog.current;
        if (modal && modal.open && modal.close) {
            try { modal.close(); } catch {}
        }
    }, []);

    useEffect(() => {
        const modal = dialog.current;
        if (!modal) return;

        if (!open) return;

        clearTimeout(closeTimeoutRef.current);
        setIsClosing(false);

        if (!modal.open && modal.showModal) {
            try {
                modal.showModal();
            } catch (e) {
                console.warn('showModal failed, using fallback');
            }
        }

        originalOverflowRef.current = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        const setVh = () => {
            const vh = window.innerHeight * 0.01;
            document.documentElement.style.setProperty('--vh', `${vh}px`);
        };

        setVh();
        window.addEventListener('resize', setVh);
        resizeHandlerRef.current = setVh;

        return () => {
            if (resizeHandlerRef.current === setVh) {
                window.removeEventListener('resize', setVh);
                resizeHandlerRef.current = null;
            }
        };
    }, [open]);

    useEffect(() => {
        const modal = dialog.current;
        if (!modal || open) return;

        const releaseModal = () => {
            setIsClosing(false);
            if (modal.open && modal.close) {
                try { modal.close(); } catch {}
            }
            document.body.style.overflow = originalOverflowRef.current || '';
            if (resizeHandlerRef.current) {
                window.removeEventListener('resize', resizeHandlerRef.current);
                resizeHandlerRef.current = null;
            }
        };

        // Browser back can clear the URL before the native dialog closes; always unlock scroll.
        if (!modal.open) {
            clearTimeout(closeTimeoutRef.current);
            releaseModal();
            return;
        }

        setIsClosing(true);
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = setTimeout(releaseModal, 280);

        return () => clearTimeout(closeTimeoutRef.current);
    }, [open]);

    const handleClick = (e) => {
        const dialogEl = dialog.current;
        if (!dialogEl) return;
        // Browsers often emit a stray click at (0,0) after the native file picker closes;
        // that sits outside the dialog rect and was incorrectly treated as a backdrop tap.
        if (e.clientX === 0 && e.clientY === 0) {
            return;
        }
        const dialogDimensions = dialogEl.getBoundingClientRect();
        if (
            e.clientX < dialogDimensions.left ||
            e.clientX > dialogDimensions.right ||
            e.clientY < dialogDimensions.top ||
            e.clientY > dialogDimensions.bottom
        ) {
            onBackdropClick?.();
        }
    };

    const modalStateClass = isClosing
        ? classes.smallModalClosing
        : open
        ? classes.smallModalOpen
        : '';

    const useBottomSheet =
        fromDeleteStudio || fromVideoFoto || equipmentOptionalSheet || paidEquipmentModal;

    const bottomSheetStateClass = useBottomSheet
        ? isClosing
            ? classes.bottomSheetClosing
            : open
            ? classes.bottomSheetOpen
            : ''
        : '';

    return createPortal(
        <dialog 
            ref={dialog} 
            className={`${classes.smallModal} ${modalStateClass} ${className} ${fromDeleteStudio ? classes.modalDeleteStudio : ''} ${fromBookingDetails && !reviewModal ? classes.modalBookingDetails : ''} ${fromBookingDetails && reviewModal ? classes.modalBookingDetailsReview : ''} ${fromRegisterPopUp ? classes.modalRegisterPopUp : ''} ${fromListStudio ? classes.modalListStudio : ''} ${useBottomSheet ? classes.bottomSheet : ''} ${bottomSheetStateClass} ${fromCreateService ? classes.modalCreateService : ''} ${fromVideoFoto ? classes.modalVideoFoto : ''} ${equipmentOptionalSheet ? classes.modalEquipmentOptionalSheet : ''} ${changeStudioModal ? classes.modalChangeStudio : ''} ${fromCreateEditService ? classes.modalCreateEditService : ''} ${paidEquipmentModal ? classes.modalPaidEquipment : ''} ${fromEditWishlist ? classes.modalEditWishlist : ''}`} 
            onClick={handleClick}
            aria-modal="true"
        >
            {children}
        </dialog>,
        document.getElementById('smallModal')
    );
};